import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useWorkspaceText } from '../interface-copy';
import { useNamespaceCopy } from '@/i18n/ns';
import editorCopy from '@/i18n/ns/workspace-editors';
import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useWorkspace } from '../context';
import { announceKnowledgePagesChanged } from '../knowledge-agent-events';
import { knowledgePagesApi, pagesErrorMessage } from './api';
import { PagePicker } from './page-picker';
import type { TreeIndex } from './tree';
import type { PageView, TreePage, WriteResult } from './types';

export type PageAction = 'move' | 'archive' | 'delete';

/**
 * Move a page under another one, archive it, or delete an archived page for
 * good. A publisher's choice happens directly; anyone else sends a move or an
 * archive as a suggestion with a one-line reason. Only publishers delete.
 */
export function PageActionDialog({
  action,
  view,
  index,
  canPublish,
  onClose,
  onDone,
}: {
  action: PageAction;
  view: PageView;
  index: TreeIndex;
  canPublish: boolean;
  onClose: () => void;
  onDone: (result: WriteResult) => void;
}) {
  const locale = useChatInterfaceLocale();
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const editor = useNamespaceCopy(editorCopy);
  const [target, setTarget] = useState<TreePage | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const page = view.page;
  // A page cannot move under itself, a page below it, or where it already is.
  const exclude = useMemo(() => {
    const blocked = index.subtree(page.id);
    blocked.add(index.byId.get(page.parentId ?? '')?.id ?? index.root.id);
    return blocked;
  }, [index, page.id, page.parentId]);
  async function submit() {
    if (busy) return;
    if (action === 'move' && !target) return setError(uiText('Choose where the page goes.'));
    if (action === 'delete' && !canPublish) return;
    if (action !== 'delete' && !canPublish && !reason.trim())
      return setError(uiText('Say in a few words why.'));
    setBusy(true);
    setError('');
    try {
      const why = canPublish ? {} : { reason: reason.trim() };
      const result =
        action === 'move'
          ? await knowledgePagesApi.move(page.id, {
              parentId: target!.isRoot ? null : target!.id,
              ...why,
            })
          : action === 'delete'
            ? await knowledgePagesApi.remove(page.id)
            : await knowledgePagesApi.archive(page.id, why);
      announceKnowledgePagesChanged();
      ws.notify(
        result.outcome === 'suggested'
          ? uiText('Your suggestion is sent. A publisher decides.')
          : action === 'move'
            ? uiText('Moved under {value1}.', { value1: target!.title })
            : action === 'delete'
              ? uiText('Deleted “{value1}”.', { value1: page.title })
              : uiText('Archived. Its history is kept.'),
        null,
        'success',
      );
      onDone(result);
    } catch (failure) {
      setError(
        pagesErrorMessage(failure, uiText('We could not do that. Please try again.'), locale),
      );
    } finally {
      setBusy(false);
    }
  }
  const title =
    action === 'move'
      ? editor.movePageTitle.replace('{title}', page.title)
      : action === 'delete'
        ? editor.deletePageTitle.replace('{title}', page.title)
        : canPublish
          ? editor.archivePageTitle.replace('{title}', page.title)
          : editor.suggestArchivePageTitle.replace('{title}', page.title);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent className="ws-portal ws-page-action-dialog" showCloseButton={!busy}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {action === 'move'
            ? uiText('Choose the page it goes under. Links to it keep working.')
            : action === 'delete'
              ? uiText(
                  'Its text, its history and any open suggestions are removed for good, and links to it from other pages stop working. This cannot be undone.',
                )
              : uiText(
                  'An archived page no longer informs answers. Its history is kept, and a publisher can restore it or delete it permanently.',
                )}
        </DialogDescription>
        {action === 'move' &&
          (target ? (
            <p className="ws-page-action-target">
              {uiText('Under')}
              <strong>{target.title}</strong>{' '}
              <button type="button" className="ws-text-button" onClick={() => setTarget(null)}>
                {uiText('Change')}
              </button>
            </p>
          ) : (
            <PagePicker
              index={index}
              label={uiText('Move under')}
              placeholder={uiText('Search pages')}
              exclude={exclude}
              onChoose={setTarget}
              onClose={onClose}
            />
          ))}
        {!canPublish && action !== 'delete' && (
          <label className="ws-page-field">
            <span>{uiText('Why?')}</span>
            <textarea
              rows={2}
              maxLength={2000}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              onInput={(event) => setReason((event.target as HTMLTextAreaElement).value)}
            />
          </label>
        )}
        {error && (
          <p className="ws-error" role="alert">
            {error}
          </p>
        )}
        <div className="ws-actions ws-page-action-buttons">
          <button type="button" className="ws-button" disabled={busy} onClick={onClose}>
            {uiText('Cancel')}
          </button>
          <button
            type="button"
            className={action === 'delete' ? 'ws-button danger' : 'ws-button primary'}
            disabled={busy || (action === 'move' && !target)}
            onClick={() => void submit()}
          >
            {busy
              ? action === 'delete'
                ? uiText('Deleting…')
                : uiText('Sending…')
              : action === 'delete'
                ? uiText('Delete permanently')
                : !canPublish
                  ? uiText('Send suggestion')
                  : action === 'move'
                    ? uiText('Move page')
                    : uiText('Archive page')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
